import React, { useState, useEffect } from "react";
import Header from "../../../Components/Header";
import Seo from "../../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { verifyKybContactToGstService } from "../../../Services/ApiTestService/apiTestService";
import { InputWithLabel, Button } from "../../../Components/Tags/Tags";
import { FiCheckCircle } from 'react-icons/fi';
import Loader from "../../../Loader";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { FaCopy, FaUser } from "react-icons/fa";
import { useService } from "../../../context/ServiceContext";

const VerifyKybContactToGST = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [gstData, setGstData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [mobileNumber, setMobileNumber] = useState("");
  const { serviceName } = useService();

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response?.data ?? {});
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleSubmit = async () => {
    // Validation
    if (!mobileNumber || mobileNumber.length !== 10) {
      showErrorToast("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        mobile: mobileNumber,
      };
      
      const response = await verifyKybContactToGstService(payload);
      
      if (response?.status?.code === 200) {
        setGstData(response?.data ?? null);
        setResponseData(response);
        setMobileNumber('');
        showSuccessToast(
          response?.message ?? 
          response?.status?.message ?? 
          "GST Verified Successfully"
        );
      } else {
        setGstData(null);
        setResponseData(null);
        showErrorToast(
          response?.message ?? 
          response?.status?.message ?? 
          response?.errors?.message ?? 
          "An unexpected error occurred."
        );
      }
    } catch (error) {
      console.error("GST Verification Error:", error);
      showErrorToast("Failed to verify GST details. Please try again.");
      setGstData(null);
      setResponseData(null);
    } finally {
      setIsLoading(false);
    }
  };

  function handleCopyText(data) {
    if (data) {
      window.navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      showSuccessToast('Copied successfully');
    }
  }

  const renderObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null;

    return Object.entries(obj).map(([key, value]) => {
      // Skip nested objects and arrays for basic display
      if (typeof value === 'object' && value !== null) {
        return null;
      }

      return (
        <div key={key} className="mb-2">
          <span className="font-bold capitalize text-blue-700">
            {key.replace(/_/g, ' ')}:
          </span>{" "}
          <span className="text-gray-700">{value ?? "N/A"}</span>
        </div>
      );
    }).filter(Boolean);
  };

  const renderArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return (
        <div className="text-gray-500 italic">No data available</div>
      );
    }

    return arr.map((item, index) => (
      <div key={index} className="pl-4 mb-3 border-l-2 border-blue-200 bg-blue-50 p-3 rounded">
        {typeof item === 'object' && item !== null ? (
          renderObject(item)
        ) : (
          <div className="text-gray-700">{item ?? "N/A"}</div>
        )}
      </div>
    ));
  };

  const renderCompanyCard = () => {
    // Safe check for gstData
    if (!gstData || !Array.isArray(gstData) || gstData.length === 0) {
      return (
        <div className="text-center p-6 bg-red-50 border border-red-200 text-red-600 rounded-lg">
          <p className="font-semibold">No GST data available.</p>
          <p className="text-sm mt-2">Please verify a valid mobile number linked with GST.</p>
        </div>
      );
    }

    const firstRecord = gstData[0];

    return (
      <div className="relative bg-white/70 backdrop-blur-lg border border-blue-100 p-6 rounded-2xl shadow-xl max-w-4xl mx-auto">
        <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
          <FiCheckCircle className="text-blue-500 text-2xl" />
          Contact To GST Details
        </h2>

        <div className="mb-4 flex items-center gap-2">
          <FaUser className="text-blue-600 text-xl" />
          <h2 className="text-xl font-bold text-blue-900">
            <span className="font-semibold">Legal Name:</span>{" "}
            {firstRecord?.legal_name ?? "N/A"}
          </h2>
        </div>

        <div className="text-gray-700 text-sm space-y-4">
          <div>
            <h3 className="font-semibold text-blue-700 mb-3 text-base">
              GST Information ({gstData.length} {gstData.length === 1 ? 'Record' : 'Records'})
            </h3>
            {renderArray(gstData)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading={serviceName} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 justify-center md:ml-80 ml-0 p-4">
        <div className="col-span-2 md:col-span-6 flex justify-end">
          <Link
            to="/TestMaster"
            className="flex items-center gap-1 text-white bg-green-600 hover:bg-green-700 px-3 py-2 rounded-md font-semibold shadow-md transition duration-300"
          >
            <span>Go Back</span>
            <IoArrowBackCircleOutline size={24} />
          </Link>
        </div>

        <InputWithLabel
          type="tel"
          label="Mobile Number"
          placeholder="Enter 10-digit Mobile Number"
          maxLength="10"
          className="p-2 rounded shadow-lg shadow-blue-500/50 border border-blue-600"
          name="mobile"
          id="mobile"
          required={true}
          value={mobileNumber}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, ''); // Only digits
            setMobileNumber(value);
          }}
        />

        <div>
          <Button
            type="button"
            className="text-center text-white bg-blue-900 shadow-lg bg-gradient-to-r from-blue-400 via-blue-600 to-blue-900 font-semibold rounded-md px-4 py-2 mt-5 text-md hover:from-blue-900 hover:to-blue-400 border-2 ms-2 disabled:opacity-50 disabled:cursor-not-allowed"
            id="verifyGST"
            name="verifyGST"
            text="Verify"
            onClick={handleSubmit}
            disabled={!mobileNumber || mobileNumber.length !== 10}
          />
        </div>
      </div>

      {gstData !== null && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4">
          {/* Main Details Card */}
          <div className="space-y-2 text-sm text-gray-700">
            <div className="overflow-auto max-h-[600px] custom-scrollbar">
              {renderCompanyCard()}
            </div>
          </div>

          {/* API Response */}
          <div>
            <h3 className="bg-green-200 text-xl font-bold my-2 rounded p-3 flex items-center justify-between">
              <span>API Response</span>
              <FaCopy 
                className="cursor-pointer bg-white p-2 rounded-md text-3xl hover:bg-gray-100 transition"
                onClick={() => handleCopyText(responseData)} 
                title="Copy Response"
              />
            </h3>
            <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-auto max-h-[600px] custom-scrollbar">
              {JSON.stringify(responseData, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <GstInstructions />
    </>
  );
};

const GstInstructions = () => {
  return (
    <div className="max-w-5xl mx-auto p-5 my-6 bg-white shadow-lg rounded-lg border border-gray-200">
      <h1 className="text-xl font-semibold mb-4 text-red-600 border-b pb-2">
        Contact to GST - Instructions
      </h1>
      <ol className="list-decimal pl-5 space-y-3 text-gray-700 text-sm">
        <li>
          <strong>Mobile Number Verification:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>Enter the mobile number linked with GST registration</li>
            <li>Incorrect numbers will result in charge deduction without results</li>
            <li>Ensure the mobile number is exactly 10 digits</li>
          </ul>
        </li>
        <li>
          <strong>Important Guidelines:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>Mobile number must be accurate and verified</li>
            <li>Do not include spaces, symbols, or country code (91)</li>
            <li>Do not close or reload the page during processing</li>
          </ul>
        </li>
        <li>
          <strong>Troubleshooting:</strong>
          <ul className="list-disc pl-5 mt-2 text-gray-600 space-y-1">
            <li>Double-check the mobile number before submitting</li>
            <li>Ensure stable internet connection</li>
            <li>Contact support if issues persist</li>
          </ul>
        </li>
      </ol>
    </div>
  );
};

export default VerifyKybContactToGST;
